import { findImagePoint } from '@utils/helpers/findImagePoint'
import { ImageProps } from 'next/image'
import React, { FC, RefObject, useEffect, useRef, useState } from 'react'

import Link from '@components/common/Link/Link'

import Tooltip from '@components/ui/Tooltip/Tooltip'

import { useShopThelookVariants } from '@features/products/api/get-shop-the-look-products'

import {
  ShopTheLookProductFragment,
  ShopTheLookProductVariantFragment,
} from '@lib/graphql/graphql'
import useMediaQuery from '@lib/hooks/useMediaQuery'
import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import { getCurrencyFromCountry } from '@lib/utils'
import { selectChannel, selectCountry } from '@redux/globalSlice'
import { mapEdgesToItems } from '@utils/map'
import { getChannelId } from 'config'
import { useSelector } from 'react-redux'
import PointCard from './PointCard'
import styles from './PointDimensions.module.scss'

export type ShopTheLookPoint = {
  id: number
  x: number
  y: number
  itemNumber: string
  product?: ShopTheLookProductFragment & {
    heroVariant: ShopTheLookProductVariantFragment
  }
}
type PointDimensionsProps = {
  mediaPicker?: MediaWithCropsFragment
  width?: number
  height?: number
  outerContainer: RefObject<HTMLDivElement>
  loaded?: boolean
  inViewport?: boolean
}

const PointDimensions: FC<PointDimensionsProps & Partial<ImageProps>> = ({
  mediaPicker,
  width,
  height,
  outerContainer,
  loaded,
  inViewport,
  ...args
}) => {
  const isSmallDevice = useMediaQuery('(max-width: 1024px)')
  const [pointDimensions, setPointDimensions] = useState<any>(null)
  const [points, setPoints] = useState<ShopTheLookPoint[] | null>([])
  const pointContainerRef = useRef<HTMLDivElement>(null)
  const country = useSelector(selectCountry)
  const channel = useSelector(selectChannel)
  const [query, setQuery] = useState<string>(null)
  const { data: shopTheLookProducts } = useShopThelookVariants({
    variables: {
      channelId: getChannelId(channel),
      currency: getCurrencyFromCountry(country.code),
      query: query,
    },
    queryConfig: {
      enabled: !!query,
    },
  })

  useEffect(() => {
    // Fetches the relative points belonging to the currently set media picker.
    setPoints(null)
    if (mediaPicker?.media?.__typename == 'Image') {
      if (mediaPicker.media.shopTheLookJson) {
        const json = mediaPicker.media.shopTheLookJson

        if (!json || json == '') return
        const points: ShopTheLookPoint[] = JSON.parse(json)
        const skus = points
          .filter((point: any) => point.itemNumber)
          .map((point: any) => point.itemNumber)
        if (skus.length === 0) return

        setQuery(skus.map((sku) => `sku:"${sku}"`).join(' OR '))
      }
    }
  }, [mediaPicker])

  useEffect(() => {
    if (shopTheLookProducts && mediaPicker?.media?.__typename == 'Image') {
      const json = mediaPicker.media.shopTheLookJson
      if (!json || json == '') return
      const points = JSON.parse(json).map((point) => {
        const product = shopTheLookProducts.find((item) =>
          mapEdgesToItems(item.variants).find((x) => x.sku == point.itemNumber),
        )
        const heroVariant = product
          ? mapEdgesToItems(product.variants).find(
              (x) => x.sku == point.itemNumber,
            )
          : null

        return {
          ...point,
          product: product ? { ...product, heroVariant } : null,
        }
      })
      setPoints(points)
    }
  }, [shopTheLookProducts])

  useEffect(() => {
    // Updates the "point container", by calculating how picture is placed in container according to its scaling (Cover | Contained) and viewport.
    if (loaded && inViewport) {
      setTimeout(() => {
        // allow media picker to update id
        requestAnimationFrame(() => {
          if (typeof window !== 'undefined') {
            if (!mediaPicker || !mediaPicker.media.id) {
              return
            }

            const image = document.getElementById(
              mediaPicker.media.id ?? '',
            ) as HTMLImageElement
            if (!image) return

            const pos = window
              .getComputedStyle(image)
              .getPropertyValue('object-position')
              .split(' ')

            const newPointDimensions = findImagePoint(
              (args.objectFit ?? 'cover') == 'contain',
              image.width,
              image.height,
              image.naturalWidth,
              image.naturalHeight,
              parseInt(pos[0]),
              parseInt(pos[1]),
            )

            setPointDimensions(newPointDimensions)

            // calculate if there is overflow
            if (
              newPointDimensions.right < window.innerWidth &&
              outerContainer.current
            ) {
              // outerContainer.current.style.overflowX = 'unset'
              // outerContainer.current.style.overflowY = 'unset'
            } else if (outerContainer.current) {
              outerContainer.current.style.removeProperty('overflow-x')
              outerContainer.current.style.removeProperty('overflow-y')
            }
          }
        })
      }, 450)
    }
  }, [width, height, loaded, inViewport])

  return points && pointDimensions ? (
    <div
      ref={pointContainerRef}
      style={{
        width: `${pointDimensions.width}px`,
        height: `${pointDimensions.height}px`,
        inset: `${pointDimensions.top}px ${pointDimensions.right}px ${pointDimensions.bottom}px ${pointDimensions.left}px`,
      }}
      className={styles.points}
    >
      {points
        .filter((point) => point.product)
        .map((point, index) =>
          !isSmallDevice ? (
            <SimplePoint
              key={point.id}
              pointContainerRef={pointContainerRef}
              point={point}
              disabled={false}
            />
          ) : (
            <Link
              key={index}
              link={{
                url: `/products/${point.product.slug}`,
              }}
              query={{ itemNumber: point.product.heroVariant.sku }}
              onClick={(e) => {
                e.stopPropagation()
                // pushImpressionTrackingEvent({
                //   products: [
                //     {
                //       id: point.item?.itemNumber?.toString(),
                //       name: point.item?.name,
                //       category: point.item?.product?.tags
                //         ?.find((tag: any) => tag.name.startsWith('type_'))
                //         .name?.split('_')[1]
                //         .replace(/-/g, ''),
                //       brand: point.item?.parent?.designer,
                //       dimension1: point.item?.itemNumber?.toString(),
                //     },
                //   ],
                //   trackinglist: 'Shop The Look',
                // })
              }}
            >
              <SimplePoint
                pointContainerRef={pointContainerRef}
                point={point}
                disabled={true}
              />
            </Link>
          ),
        )}
    </div>
  ) : (
    <></>
  )
}

const SimplePoint: FC<{
  point: ShopTheLookPoint
  pointContainerRef?: React.RefObject<HTMLDivElement>
  disabled: boolean
}> = ({ point, pointContainerRef, disabled }) => {
  return (
    <div
      style={{
        top: `${point.y * 100}%`,
        left: `${point.x * 100}%`,
      }}
      key={point.id}
      className={styles.point}
    >
      <Tooltip
        className={styles.tooltip}
        disabled={disabled}
        placement="bottom"
        hideArrow
        content={<PointCard point={point} />}
      >
        <div className={styles.requestloader}>
          <div className={styles.std}></div>
        </div>
      </Tooltip>
    </div>
  )
}

export default PointDimensions
